import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Linkedin, Instagram, Youtube, Sun, Moon } from 'lucide-react';
import logoAsset from '@/assets/r8n-logo.png.asset.json';
import { useLang } from '@/i18n/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { altPath, pathFor } from '@/i18n/routes';
import { navLabels, navOrder, ui } from '@/i18n/content/ui';

const socialLinks = [
  { href: 'https://www.linkedin.com/in/rot8ion', label: 'LinkedIn', Icon: Linkedin },
  { href: 'https://www.instagram.com/rot8ion/', label: 'Instagram', Icon: Instagram },
  { href: 'https://www.youtube.com/@rot8ion', label: 'YouTube', Icon: Youtube },
];

function FlagIcon({ lang }: { lang: 'nl' | 'en' }) {
  const common = 'h-3.5 w-5 rounded-[2px] ring-1 ring-black/10 shadow-sm';
  if (lang === 'nl') {
    return (
      <svg viewBox="0 0 9 6" className={common} role="img" aria-hidden="true">
        <rect width="9" height="6" fill="#21468B" />
        <rect width="9" height="4" fill="#FFF" />
        <rect width="9" height="2" fill="#AE1C28" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 60 30" className={common} role="img" aria-hidden="true">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFF" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
      <path d="M30,0 V30 M0,15 H60" stroke="#FFF" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function NavLabel({ label }: { label: string }) {
  if (label.includes('\n')) {
    const [top, bottom] = label.split('\n');
    return (
      <span className="flex flex-col items-center leading-none gap-0.5">
        <span className="text-sm tracking-[0.12em]">{top}</span>
        <span className="text-sm tracking-[0.12em]">{bottom}</span>
      </span>
    );
  }
  return <span>{label}</span>;
}

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const lang = useLang();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const t = ui[lang];
  const labels = navLabels[lang];
  const alt = altPath(location.pathname) ?? {
    lang: lang === 'nl' ? ('en' as const) : ('nl' as const),
    path: pathFor('visualisatie', lang === 'nl' ? 'en' : 'nl'),
  };

  const itemClass = ({ isActive }: { isActive: boolean }) =>
    [
      'text-sm tracking-wide transition-colors',
      isActive
        ? 'text-[var(--viz-accent)]'
        : 'text-sitefg/60 hover:text-[var(--viz-accent)]',
    ].join(' ');

  const languageSwitch = (
    <Link
      to={alt.path}
      onClick={() => setOpen(false)}
      title={t.switchLanguage}
      aria-label={t.switchLanguage}
      className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-sitefg/50 hover:text-[var(--viz-accent)] transition-colors"
    >
      <FlagIcon lang={alt.lang} />
      <span>{alt.lang === 'en' ? 'EN' : 'NL'}</span>
    </Link>
  );

  const themeSwitch = (
    <button
      type="button"
      onClick={toggleTheme}
      title={isDark ? t.switchToLight : t.switchToDark}
      aria-label={isDark ? t.switchToLight : t.switchToDark}
      className="text-sitefg/50 hover:text-[var(--viz-accent)] transition-colors"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );

  return (
    <nav className="sticky top-0 z-40 bg-sitebg/95 backdrop-blur border-b border-sitefg/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 md:h-16 flex items-center justify-between">
        <Link
          to={pathFor('nieuwwerk', lang)}
          className="flex items-center flex-shrink-0"
          onClick={() => setOpen(false)}
        >
          <img
            src={logoAsset.url}
            alt="Perpetual Plexus"
            className="h-9 w-auto dark:drop-shadow-[0_0_4px_rgba(255,255,255,0.55),0_0_10px_rgba(255,255,255,0.25)]"
          />
        </Link>

        <div className="hidden md:flex items-center gap-2 lg:gap-4 xl:gap-6">
          {navOrder.map((key) => (
            <NavLink
              key={key}
              to={pathFor(key, lang)}
              end={key === 'visualisatie'}
              className={itemClass}
            >
              <NavLabel label={labels[key]} />
            </NavLink>
          ))}
          <div className="flex items-center gap-1.5 lg:gap-2 xl:gap-3 pl-2 lg:pl-3 xl:pl-4 border-l border-sitefg/10">
            {socialLinks.map(({ href, label, Icon }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-sitefg/50 hover:text-[var(--viz-accent)] transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3 pl-2 lg:pl-3 xl:pl-4 border-l border-sitefg/10">{themeSwitch}{languageSwitch}</div>
        </div>

        <div className="flex items-center gap-4 md:hidden">
          {themeSwitch}
          {languageSwitch}
          <button
            type="button"
            className="text-sitefg/70"
            aria-label={open ? t.closeMenu : t.openMenu}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-sitefg/10 px-6 py-3 flex flex-col gap-3">
          {navOrder.map((key) => (
            <NavLink
              key={key}
              to={pathFor(key, lang)}
              end={key === 'visualisatie'}
              className={itemClass}
              onClick={() => setOpen(false)}
            >
              <NavLabel label={labels[key]} />
            </NavLink>
          ))}
          <div className="flex items-center gap-4 pt-2 border-t border-sitefg/10">
            {socialLinks.map(({ href, label, Icon }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-sitefg/50 hover:text-[var(--viz-accent)] transition-colors"
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
